import React, { useContext, useMemo, useCallback, useEffect } from 'react';

import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';

import { Router } from '@techstyle/next-routes';
import { FormattedMessage, useIntl } from '@techstyle/react-intl';

import { trackByoModalOptionsClick } from '../../actions/byo';
import { addFirstByoItem } from '../../actions/checkout';
import { mobile, desktop } from '../../styles';
import { BasicButton } from '../../styles/button';
import { BasicModal } from '../../styles/modal';
import { getByoSetsByProduct } from '../../utils/selectors';
import { Component as ByoProvider } from '../ByoProvider';
import ToastContext from '../ToastContext/ToastContext';

const dialogStyle = css`
  ${desktop`
    width: 500px;
    border-radius: 8px;
    box-shadow: 0px 4px 8px 0px ${({ theme }) => theme.colors.lightShadow4};
  `}
  width: 100%;
  border-radius: 8px 8px 0 0;
  padding: ${({ theme }) => theme.spacing.small}px;
  box-shadow: 0px -2px 8px 0px ${({ theme }) => theme.colors.lightShadow4};
`;

const StyledModal = styled(BasicModal).attrs({
  springConfig: {
    tension: 230,
  },
  dialogStyle,
  transitionFrom: {
    opacity: 0,
    transform: 'translate3d(0, 160px, 0)',
  },
  transitionEnter: {
    opacity: 1,
    transform: 'translate3d(0, 0, 0)',
  },
  transitionLeave: {
    opacity: 0,
    transform: 'translate3d(0, 160px, 0)',
  },
})`
  ${mobile`
    ${BasicModal.Wrapper} {
      display: flex;
      align-items: flex-end;
    }
  `}
`;

const ModalContent = styled.div``;

const ModalHeader = styled.h1`
  ${mobile`font-size: 20px`}
  line-height: 1.2;
`;

const ModalSubTitle = styled.div`
  font-size: 14px;
  font-weight: 300;
  ${desktop`
    font-size: 16px
  `}
`;

const OptionButtonsContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  margin-top: ${({ theme }) => theme.spacing.small}px;
`;

const optionButtonStyle = css`
  display: flex;
  flex-direction: column;
  height: auto;
  padding: ${({ theme }) => theme.spacing.tiny}px
    ${({ theme }) => theme.spacing.small}px;
  background-color: ${({ theme }) => theme.colors.active};
  border-color: ${({ theme }) => theme.colors.active};
`;

const OptionButton = styled(BasicButton).attrs({
  buttonStyle: optionButtonStyle,
})`
  width: ${props =>
    props.isSingle ? '100%' : `calc(50% - ${props.theme.spacing.tiny}px)`};
`;

const ByoSetName = styled.div`
  opacity: 0.85;
  font-size: 10px;
  font-weight: 400;
  letter-spacing: 1.75px;
  line-height: 1;
  margin-bottom: 4px;
  ${desktop`
    font-size: 12px
  `}
`;

const OptionCategoriesLabel = styled.div`
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.3px;
  ${desktop`
    font-size: 14px
  `}
`;

export default function ByoOptionsModal({ masterProduct, productToAdd }) {
  const intl = useIntl();
  const dispatch = useDispatch();
  const availableByoSets = useSelector(state =>
    getByoSetsByProduct(state, { product: masterProduct })
  );
  const { showMessage: showToastMessage } = useContext(ToastContext.Context);
  const {
    closeByoOptionsModal,
    isByoOptionsModalOpen,
    initiateByoFlow,
    increaseByoStep,
    setByoGroupCode,
    setIsByoCtaEnabled,
    setIsExitModalOpen,
    setIsLoading,
    trackAddedItems,
  } = useContext(ByoProvider.Context);

  // hide byo options modal when unmount
  useEffect(() => {
    return () => closeByoOptionsModal();
  }, [closeByoOptionsModal]);

  const handleOptionButtonClick = useCallback(
    async (byoSet, firstByoGridsUrl) => {
      closeByoOptionsModal();
      if (productToAdd && firstByoGridsUrl) {
        setIsByoCtaEnabled(false);
        setIsLoading(true);
        // sometimes exit modal pop up randomly when a new flow starts, set it to close when starting a new flow
        setIsExitModalOpen(false);
        dispatch(trackByoModalOptionsClick(byoSet.label));
        try {
          const result = await dispatch(
            addFirstByoItem(productToAdd.product_id, byoSet.byoProductId, 1)
          );
          if (result && result.payload && result.payload.lastByoGroupCode) {
            initiateByoFlow({
              byoSet,
              isInterstitialFlow: false,
              firstProductMpid: productToAdd.master_product_id,
            });
            setByoGroupCode(result.payload.lastByoGroupCode);
            trackAddedItems(masterProduct);
            increaseByoStep();
            // only push to byo grids page after first item is added to ECHO cart successfully
            Router.push(
              `${firstByoGridsUrl}?lastMasterProductId=${masterProduct.master_product_id}`
            );
          }
        } catch (err) {
          setIsByoCtaEnabled(true);
          showToastMessage(
            <FormattedMessage
              id="site_byo.general_error"
              defaultMessage="Oops we’ve encountered an error. Try again"
            />
          );
        } finally {
          setIsLoading(false);
        }
      }
    },
    [
      setIsByoCtaEnabled,
      setIsExitModalOpen,
      closeByoOptionsModal,
      productToAdd,
      setIsLoading,
      dispatch,
      initiateByoFlow,
      setByoGroupCode,
      showToastMessage,
      trackAddedItems,
      masterProduct,
      increaseByoStep,
    ]
  );

  const optionButtons = useMemo(
    () =>
      availableByoSets.map((byoSet, index) => {
        const firstByoGridsUrl = byoSet.combinationOptions[0]?.byoGridsUrl;
        return (
          <OptionButton
            key={`ByoOptionButtons-${byoSet.byoProductId}`}
            buttonStyle={optionButtonStyle}
            data-autotag={`additem_cta_${index + 1}`}
            isSingle={availableByoSets.length === 1}
            onClick={() => handleOptionButtonClick(byoSet, firstByoGridsUrl)}
          >
            <ByoSetName>{byoSet.label}</ByoSetName>
            <OptionCategoriesLabel>
              {byoSet.startCategory.optionRb
                ? intl.formatMessage(byoSet.startCategory.optionRb)
                : null}
            </OptionCategoriesLabel>
          </OptionButton>
        );
      }),
    [availableByoSets, intl, handleOptionButtonClick]
  );

  return (
    <StyledModal
      isOpen={isByoOptionsModalOpen}
      onExit={closeByoOptionsModal}
      titleId="byo_options_modal"
    >
      <ModalContent data-autotag="pick_your_set_modal">
        <ModalHeader id="byo_options_modal">
          <FormattedMessage
            id="site_pdp.byo_modal_header"
            defaultMessage="Build a Set"
          />
        </ModalHeader>
        <ModalSubTitle>
          <FormattedMessage
            id="site_pdp.byo_modal_subtitle"
            defaultMessage="Select an option to continue."
          />
        </ModalSubTitle>
        <OptionButtonsContainer>{optionButtons}</OptionButtonsContainer>
      </ModalContent>
    </StyledModal>
  );
}

ByoOptionsModal.propTypes = {
  masterProduct: PropTypes.object,
  productToAdd: PropTypes.object,
};
